// 채팅 스트리밍 상태 머신.
//
// startChatStream의 이벤트를 받아 메시지/근거/스트리밍 상태로 변환한다.
// ai_message 토큰은 rAF로 버퍼링해 프레임당 한 번만 setState 해서 렌더 폭주를 막는다.

import { useCallback, useEffect, useRef, useState } from 'react'
import { startChatStream } from '~/api/chat-stream'
import type { ChatProfile, Citation, StreamEvent } from '~/api/types'

export type ChatRole = 'user' | 'assistant'

export interface ChatMessage {
    id: string
    role: ChatRole
    content: string
    /** 스트리밍 중인 assistant 메시지 여부. */
    streaming?: boolean
    /** 이 메시지에 사용된 근거(저장 세션 복원용). 우측 근거 패널 재구성에 쓴다. */
    citations?: Citation[]
}

/** 스트리밍 중 사람이 이해할 도구 사용 문구. */
export interface ToolActivity {
    name: string
    /** 사람이 읽을 라벨(예: "HACCP 규정집을 찾는 중…"). */
    label: string
}

const TOOL_LABELS: Record<string, string> = {
    knowledge_search: 'HACCP 규정집을 찾는 중…',
    assess_scale: '사업장 규모를 판정하는 중…',
    find_industry: '적용대상 업종을 찾는 중…',
    self_check: '자가진단 문항을 준비하는 중…',
    readiness_assessment: '인증 준비도를 진단하는 중…',
    plan_review: '관리계획서를 검토하는 중…',
    escalate: '공식 창구로 연결하는 중…',
}

function toolLabel(name: string): string {
    return TOOL_LABELS[name] ?? `${name} 도구를 실행하는 중…`
}

function makeId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export interface UseChatStreamOptions {
    profile: ChatProfile
    sessionId?: string
    businessContextId?: string
    /** 시드 메시지. 저장된 세션을 열 때 초기 대화로 채운다. */
    initialMessages?: ChatMessage[]
    /** done 이벤트로 sessionId가 처음 생기거나 바뀌면 호출된다(사이드바 갱신용). */
    onSessionId?(sessionId: string): void
}

export interface UseChatStreamResult {
    messages: ChatMessage[]
    citations: Citation[]
    /** 도구가 제시한 선택지(예: 업종 후보). 클릭하면 그 값으로 바로 답한다(HITL). */
    pendingChoices: string[]
    toolActivity: ToolActivity | null
    isStreaming: boolean
    error: string | null
    sessionId: string | undefined
    sendMessage(text: string): void
    stop(): void
    reset(): void
}

export function useChatStream(
    options: UseChatStreamOptions
): UseChatStreamResult {
    const { profile, businessContextId, onSessionId } = options

    const [messages, setMessages] = useState<ChatMessage[]>(
        () => options.initialMessages ?? []
    )
    const [citations, setCitations] = useState<Citation[]>([])
    const [pendingChoices, setPendingChoices] = useState<string[]>([])
    const [toolActivity, setToolActivity] = useState<ToolActivity | null>(null)
    const [isStreaming, setIsStreaming] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sessionId, setSessionId] = useState<string | undefined>(
        options.sessionId
    )

    const abortRef = useRef<AbortController | null>(null)

    // onSessionId는 호출부에서 매 렌더 새 함수가 와도 handleEvent 의존성이 흔들리지
    // 않도록 ref에 담는다. sessionIdRef는 실제 값 변화 시에만 콜백을 쏘기 위한 기준.
    const onSessionIdRef = useRef(onSessionId)
    onSessionIdRef.current = onSessionId
    const sessionIdRef = useRef<string | undefined>(options.sessionId)

    // 세션을 바꿀 때만 메시지를 그 세션 히스토리로 교체한다. 같은 세션 안에서는
    // sendMessage가 append로 누적하므로, initialMessages 참조가 바뀐 경우에만 동기화한다.
    // (useState 초기화 함수는 마운트 때 한 번만 실행돼 세션 전환 시 갱신되지 않는 문제 방지)
    const initialMessagesRef = useRef(options.initialMessages)
    if (initialMessagesRef.current !== options.initialMessages) {
        initialMessagesRef.current = options.initialMessages
        setMessages(options.initialMessages ?? [])
        // 세션 전환이므로 멀티턴 이어가기용 thread_id도 그 세션 값으로 재설정한다.
        setSessionId(options.sessionId)
        sessionIdRef.current = options.sessionId
        // 저장된 메시지에 실린 근거를 우측 패널로 복원한다(예전 답변의 근거 자료 재현).
        setCitations(
            (options.initialMessages ?? []).flatMap((m) => m.citations ?? [])
        )
        setPendingChoices([])
    }

    // rAF 버퍼: 토큰을 모았다가 프레임당 한 번 flush 한다.
    const pendingTextRef = useRef('')
    const rafRef = useRef<number | null>(null)
    const streamingIdRef = useRef<string | null>(null)

    const flush = useCallback(() => {
        rafRef.current = null
        const chunk = pendingTextRef.current
        if (!chunk) return
        pendingTextRef.current = ''
        const id = streamingIdRef.current
        if (!id) return
        setMessages((prev) =>
            prev.map((m) =>
                m.id === id ? { ...m, content: m.content + chunk } : m
            )
        )
    }, [])

    const scheduleFlush = useCallback(() => {
        if (rafRef.current != null) return
        const raf =
            typeof requestAnimationFrame === 'function'
                ? requestAnimationFrame
                : (cb: FrameRequestCallback) =>
                      setTimeout(
                          () => cb(performance.now()),
                          16
                      ) as unknown as number
        rafRef.current = raf(flush)
    }, [flush])

    const cancelRaf = useCallback(() => {
        if (
            rafRef.current != null &&
            typeof cancelAnimationFrame === 'function'
        ) {
            cancelAnimationFrame(rafRef.current)
        }
        rafRef.current = null
    }, [])

    const stop = useCallback(() => {
        abortRef.current?.abort()
        abortRef.current = null
        cancelRaf()
        flush()
        setIsStreaming(false)
        setToolActivity(null)
        const id = streamingIdRef.current
        if (id) {
            setMessages((prev) =>
                prev.map((m) => (m.id === id ? { ...m, streaming: false } : m))
            )
        }
        streamingIdRef.current = null
    }, [cancelRaf, flush])

    const handleEvent = useCallback(
        (event: StreamEvent) => {
            switch (event.type) {
                case 'tool_call':
                    setToolActivity({
                        name: event.name,
                        label: toolLabel(event.name),
                    })
                    break
                case 'tool_result': {
                    const found = event.artifact?.citations ?? []
                    if (found.length)
                        setCitations((prev) => [...prev, ...found])
                    // 도구가 후보 목록을 주면 클릭형 선택지로 노출(예: find_industry 업종 후보).
                    const cands = event.artifact?.candidates
                    if (Array.isArray(cands)) {
                        const opts = cands.filter(
                            (c): c is string => typeof c === 'string' && !!c
                        )
                        if (opts.length) setPendingChoices(opts)
                    }
                    setToolActivity(null)
                    break
                }
                case 'ai_message':
                    pendingTextRef.current += event.content
                    scheduleFlush()
                    break
                case 'citation': {
                    const citation: Citation = {
                        documentId: event.documentId,
                        title: event.title,
                        page: event.page,
                        clause: event.clause,
                        snippet: event.snippet,
                        score: event.score,
                    }
                    setCitations((prev) => [...prev, citation])
                    break
                }
                case 'done':
                    if (event.sessionId) {
                        setSessionId(event.sessionId)
                        if (event.sessionId !== sessionIdRef.current) {
                            sessionIdRef.current = event.sessionId
                            onSessionIdRef.current?.(event.sessionId)
                        }
                    }
                    break
                case 'suggestions': {
                    // 답변 뒤 도착하는 후속 질문 제안 → 클릭형 선택지로 노출(done 후 이벤트).
                    const items = event.items.filter((s) => !!s?.trim())
                    if (items.length) setPendingChoices(items)
                    break
                }
                case 'error':
                    setError(event.message)
                    break
            }
        },
        [scheduleFlush]
    )

    const sendMessage = useCallback(
        (text: string) => {
            const trimmed = text.trim()
            if (!trimmed || isStreaming) return

            setError(null)
            setToolActivity(null)
            setPendingChoices([]) // 새 턴 시작 → 이전 선택지 제거

            const userMessage: ChatMessage = {
                id: makeId(),
                role: 'user',
                content: trimmed,
            }
            const assistantId = makeId()
            const assistantMessage: ChatMessage = {
                id: assistantId,
                role: 'assistant',
                content: '',
                streaming: true,
            }
            streamingIdRef.current = assistantId
            setMessages((prev) => [...prev, userMessage, assistantMessage])
            setIsStreaming(true)

            const controller = new AbortController()
            abortRef.current = controller

            void (async () => {
                try {
                    for await (const event of startChatStream(
                        profile,
                        // sessionId 상태에는 done 이벤트로 받은 thread_id가 들어 있다(멀티턴).
                        {
                            message: trimmed,
                            threadId: sessionId,
                            businessContextId,
                        },
                        controller.signal
                    )) {
                        handleEvent(event)
                    }
                } catch (err) {
                    if (!controller.signal.aborted) {
                        setError(
                            err instanceof Error
                                ? err.message
                                : '스트리밍 중 오류가 발생했어요.'
                        )
                    }
                } finally {
                    cancelRaf()
                    flush()
                    setIsStreaming(false)
                    setToolActivity(null)
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? { ...m, streaming: false }
                                : m
                        )
                    )
                    streamingIdRef.current = null
                    abortRef.current = null
                }
            })()
        },
        [
            businessContextId,
            cancelRaf,
            flush,
            handleEvent,
            isStreaming,
            profile,
            sessionId,
        ]
    )

    const reset = useCallback(() => {
        stop()
        setMessages([])
        setCitations([])
        setPendingChoices([])
        setError(null)
        setSessionId(options.sessionId)
        sessionIdRef.current = options.sessionId
    }, [options.sessionId, stop])

    useEffect(() => {
        return () => {
            abortRef.current?.abort()
            cancelRaf()
        }
    }, [cancelRaf])

    return {
        messages,
        citations,
        pendingChoices,
        toolActivity,
        isStreaming,
        error,
        sessionId,
        sendMessage,
        stop,
        reset,
    }
}
